import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Select, 
  DatePicker, 
  Space, 
  Typography,
  Table,
  Progress,
  Tag,
  Statistic
} from 'antd';
import { 
  TeamOutlined, 
  UserOutlined 
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useQuery } from 'react-query';
import axios from 'axios';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const TeamWorkload = () => {
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, 'day'),
    dayjs()
  ]);
  const [selectedUser, setSelectedUser] = useState('');

  // 获取工作统计数据
  const { data: workStatsData, isLoading } = useQuery(
    ['workStats', dateRange, selectedUser],
    () => axios.get('/daily-logs/stats', {
      params: {
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        userId: selectedUser || undefined
      }
    }).then(res => res.data.data),
    { refetchInterval: 60000 }
  );

  // 获取用户列表
  const { data: usersData } = useQuery(
    'users',
    () => axios.get('/auth/users').then(res => res.data.data)
  );

  // 处理图表数据
  const chartData = workStatsData?.workStats?.map(stat => ({
    name: stat.user?.realName || '未知用户',
    totalHours: parseFloat(stat.totalHours) || 0,
    avgHours: parseFloat(stat.avgHours) || 0,
    logCount: parseInt(stat.logCount) || 0,
    position: stat.user?.position || '未知'
  })) || [];

  // 工作负荷分类
  const getWorkloadLevel = (avgHours) => {
    if (avgHours >= 8) return { level: 'high', text: '高负荷', color: '#ff4d4f' };
    if (avgHours >= 6) return { level: 'medium', text: '正常', color: '#faad14' };
    if (avgHours >= 3) return { level: 'light', text: '轻松', color: '#52c41a' };
    return { level: 'idle', text: '空闲', color: '#d9d9d9' };
  };

  // 饼图数据
  const pieData = [
    { name: '高负荷', value: chartData.filter(d => getWorkloadLevel(d.avgHours).level === 'high').length, color: '#ff4d4f' },
    { name: '正常', value: chartData.filter(d => getWorkloadLevel(d.avgHours).level === 'medium').length, color: '#faad14' },
    { name: '轻松', value: chartData.filter(d => getWorkloadLevel(d.avgHours).level === 'light').length, color: '#52c41a' },
    { name: '空闲', value: chartData.filter(d => getWorkloadLevel(d.avgHours).level === 'idle').length, color: '#d9d9d9' }
  ].filter(item => item.value > 0);

  const workloadColumns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <UserOutlined />
          <div>
            <Text strong>{text}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.position}
            </Text>
          </div>
        </Space>
      )
    },
    {
      title: '工作负荷',
      key: 'workload',
      render: (_, record) => {
        const workload = getWorkloadLevel(record.avgHours);
        return (
          <Tag color={workload.color}>
            {workload.text}
          </Tag>
        );
      }
    },
    {
      title: '总工时',
      dataIndex: 'totalHours',
      key: 'totalHours',
      render: (hours) => `${hours.toFixed(1)}h`,
      sorter: (a, b) => a.totalHours - b.totalHours
    },
    {
      title: '日均工时',
      dataIndex: 'avgHours',
      key: 'avgHours',
      render: (hours) => `${hours.toFixed(1)}h`,
      sorter: (a, b) => a.avgHours - b.avgHours
    },
    {
      title: '工时利用率',
      key: 'utilization',
      render: (_, record) => {
        const utilization = Math.min((record.avgHours / 8) * 100, 100);
        return (
          <Progress 
            percent={utilization} 
            size="small"
            status={utilization > 100 ? 'exception' : 'active'}
            format={percent => `${percent.toFixed(0)}%`}
          />
        );
      }
    },
    {
      title: '填报天数',
      dataIndex: 'logCount',
      key: 'logCount',
      render: (count) => `${count}天`
    }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: 'white',
          padding: '12px',
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <Text strong>{label}</Text>
          <br />
          <Text>总工时: {payload[0].value.toFixed(1)} 小时</Text>
          <br />
          <Text>日均工时: {payload[0].payload.avgHours.toFixed(1)} 小时</Text>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title={
          <Space>
            <TeamOutlined />
            团队工作负荷分析
          </Space>
        }
        className="status-card"
      >
        {/* 筛选条件 */}
        <div style={{ marginBottom: '24px' }}>
          <Space wrap>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
              placeholder={['开始日期', '结束日期']}
            />
            <Select
              placeholder="选择团队成员"
              allowClear
              style={{ width: 150 }}
              onChange={setSelectedUser}
            >
              {usersData?.users?.map(user => (
                <Option key={user.id} value={user.id}>
                  {user.realName}
                </Option>
              ))}
            </Select>
          </Space>
        </div>

        {/* 统计概览 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="团队总工时"
                value={chartData.reduce((sum, item) => sum + item.totalHours, 0)}
                precision={1}
                suffix="小时"
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="平均工时"
                value={chartData.length > 0 ? 
                  chartData.reduce((sum, item) => sum + item.totalHours, 0) / chartData.length : 0
                }
                precision={1}
                suffix="小时/人"
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="高负荷人员"
                value={chartData.filter(d => getWorkloadLevel(d.avgHours).level === 'high').length}
                suffix="人"
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="活跃天数"
                value={dateRange[1].diff(dateRange[0], 'day') + 1}
                suffix="天"
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>

        {/* 图表区域 */}
        <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
          <Col xs={24} lg={16}>
            <Card title="团队工时分布" size="small">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    label={{ value: '工时(小时)', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="totalHours" 
                    fill="#1890ff" 
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          
          <Col xs={24} lg={8}>
            <Card title="负荷分布" size="small">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value, percent }) => `${name}: ${value}人 (${(percent).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* 详细数据表格 */}
        <Card title="团队成员工作详情" size="small">
          <Table
            columns={workloadColumns}
            dataSource={chartData}
            loading={isLoading}
            rowKey="name"
            pagination={false}
            size="small"
          />
        </Card>
      </Card>
    </div>
  );
};

export default TeamWorkload;
